import { motion } from "framer-motion";
import { FiCheck, FiAward, FiShield, FiClock } from "react-icons/fi";
import { NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay: d, ease: "easeOut" },
  viewport: { once: true, amount: 0.4 },
});

function useCountUp(target, duration = 1500) {
  const [count, setCount] = useState(0);
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.4 });

  useEffect(() => {
    if (inView) {
      let start = 0;
      const increment = target / (duration / 16);

      const timer = setInterval(() => {
        start += increment;
        if (start >= target) {
          start = target;
          clearInterval(timer);
        }
        setCount(parseFloat(start.toFixed(1)));
      }, 16);

      return () => clearInterval(timer);
    }
  }, [inView, target, duration]);

  return { ref, count };
}

function ReadMoreBlock({ children, to = "/about", lines = 4 }) {
  return (
    <div className="relative">
      <div
        className="mt-4 text-white/80 md:text-lg leading-relaxed max-w-3xl"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: lines,
          WebkitBoxOrient: "vertical",
          maskImage: "linear-gradient(180deg, #000 60%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(180deg, #000 60%, transparent 100%)",
        }}
      >
        {children}
      </div>

      <NavLink
        to="/about"
        className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-white/90 hover:text-white underline underline-offset-4"
      >
        + Read more
      </NavLink>
    </div>
  );
}

export default function About() {
  return (
    <section id="about" className="bg-transparent relative scroll-mt-24">
      <div className="absolute -top-10 inset-x-0 h-10 bg-gradient-to-b from-white/10 to-transparent pointer-events-none" />
      <div className="relative bg-transparent scroll-mt-24 py-10">
        <div className="container">
          <div className="grid md:grid-cols-12 gap-10 md:gap-12 items-center">
            <motion.div className="md:col-span-7" {...fade(0)}>
              <p className="text-[11px] md:text-xs tracking-[0.28em] uppercase text-white/70">
                About Us
              </p>
              <h2 className="mt-3 text-3xl md:text-5xl font-extrabold text-white leading-tight">
                Engineered aluminium
                <br className="hidden md:block" />
                windows, doors & facades.
              </h2>

              <ReadMoreBlock to="/about" lines={6}>
                We design, fabricate and install high-performance glazing
                systems with slim sightlines, acoustic comfort and long-term
                durability. From premium residences to commercial facades, our
                team handles design-assist, manufacturing and on-site
                commissioning end-to-end. We work with thermally broken systems,
                marine-grade finishes and tested hardware to deliver performance
                that lasts in real-world use.
              </ReadMoreBlock>

              <ul className="mt-6 space-y-3 text-white/85">
                {[
                  "Thermally efficient profiles & multi-chamber sealing",
                  "Tested hardware and marine-grade finishes",
                  "Custom sizes, corner windows, large sliders",
                ].map((t, i) => (
                  <motion.li
                    key={i}
                    className="flex items-start gap-3"
                    {...fade(0.05 * i + 0.1)}
                  >
                    <span className="mt-1 shrink-0 inline-flex items-center justify-center h-5 w-5 rounded-sm bg-white/15">
                      <FiCheck className="text-white" size={14} />
                    </span>
                    <span>{t}</span>
                  </motion.li>
                ))}
              </ul>

              <div className="mt-8 flex flex-wrap gap-4">
                <NavLink
                  to="/products"
                  className="inline-flex items-center justify-center px-5 py-3 rounded-md bg-white text-black font-semibold hover:bg-white/90"
                >
                  Explore Products
                </NavLink>
                <NavLink
                  to="/contact"
                  className="inline-flex items-center justify-center px-5 py-3 rounded-md border border-white/20 text-white hover:bg-white/10"
                >
                  Get a Quote
                </NavLink>
              </div>
            </motion.div>

            <div className="md:col-span-5">
              <div className="grid grid-cols-2 gap-4">
                <StatCard
                  icon={<FiAward />}
                  label="Projects Delivered"
                  value="450+"
                  delay={0.1}
                />
                <StatCard
                  icon={<FiShield />}
                  label="Warranty"
                  value="10 Years"
                  delay={0.2}
                />
                <StatCard
                  icon={<FiClock />}
                  label="On-time Install"
                  value="98%"
                  delay={0.3}
                />
                <StatCard
                  icon={<FiCheck />}
                  label="Customer Rating"
                  value="4.8/5"
                  delay={0.4}
                />
              </div>

              <motion.div
                className="mt-4 rounded-lg bg-white/5 border border-white/10 p-4 text-white/80"
                {...fade(0.45)}
              >
                We partner with leading system suppliers and follow ISO-grade
                fabrication practices to ensure performance that lasts.
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatCard({ icon, label, value, delay = 0 }) {
  const match = value.match(/^([0-9.]+)(.*)$/);
  const numericValue = match ? parseFloat(match[1]) : 0;
  const suffix = match ? match[2] : "";

  const { ref, count } = useCountUp(numericValue);

  return (
    <motion.div
      ref={ref}
      className="rounded-xl bg-white/5 border border-white/10 p-5 text-center"
      initial={{ opacity: 0, y: 14, scale: 0.98 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, delay }}
      viewport={{ once: true, amount: 0.4 }}
    >
      <div className="mx-auto mb-2 inline-flex h-10 w-10 items-center justify-center rounded-md bg-white/10">
        {icon}
      </div>
      <div className="text-2xl font-extrabold text-white">
        {count}
        {suffix}
      </div>
      <div className="mt-1 text-xs tracking-wide text-white/70 uppercase">
        {label}
      </div>
    </motion.div>
  );
}
